import {
  UsersIcon,
  DropdownMenu,
  Flex,
  Heading,
  Skeleton,
  Text,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import { useOrganizationSwitcherContext } from './OrganizationSwitcherContext'

import styles from './OrganizationSwitcher.module.css'

import type { JSX } from 'react'

/**
 * Formats plan ID to display name (e.g., "essentials" -> "Essentials plan")
 */
function formatPlanName(planId: string | null | undefined): string | null {
  if (!planId) return null
  return `${planId.charAt(0).toUpperCase()}${planId.slice(1)} plan`
}

/**
 * Locked channels sit outside the paid channel quantity, so counting them
 * makes the number read as more channels than the subscription covers.
 */
function countEnabledChannels(
  channels: ReadonlyArray<{ isLocked: boolean }> | null | undefined,
): number {
  return channels?.filter((channel) => !channel.isLocked).length ?? 0
}

/**
 * Renders the content for an organization item
 * Shared across all organization display scenarios
 */
function OrganizationItemContent({
  name,
  channelCount,
  memberCount,
  planName,
  isOwnedByCurrentUser,
  className,
}: {
  name: string
  channelCount: number
  memberCount?: number
  planName?: string | null
  isOwnedByCurrentUser: boolean
  className?: string
}): JSX.Element {
  const formattedPlanName = formatPlanName(planName)

  return (
    <Flex gap="2xs" direction="column" className={className}>
      <Heading
        size="xs"
        as="p"
        translate="no"
        icon={isOwnedByCurrentUser ? undefined : <UsersIcon size="xsmall" />}
        iconPosition="end"
      >
        {name}
      </Heading>
      <Text
        as="small"
        color="subtle"
        size="sm"
        className={styles.organizationItemText}
      >
        <span>{formattedPlanName && <>{formattedPlanName} &middot; </>}</span>
        <span>
          {channelCount === 1 ? '1 channel' : `${channelCount} channels`}
        </span>
        {memberCount !== undefined && (
          <>
            {' '}
            &middot;
            <Flex align="center" gap="2xs" className={styles.memberCount}>
              {memberCount} <UsersIcon size="xsmall" />
            </Flex>
          </>
        )}
      </Text>
    </Flex>
  )
}

/**
 * Wrapper component that always renders the email label
 * and wraps children in a DropdownMenu.Group
 */
function OrganizationSwitcherWrapper({
  email,
  children,
}: {
  email: string | null | undefined
  children: React.ReactNode
}): JSX.Element {
  return (
    <DropdownMenu.Group>
      <DropdownMenu.Label className={styles.emailLabel}>
        {email}
      </DropdownMenu.Label>
      {children}
    </DropdownMenu.Group>
  )
}

export function OrganizationSwitcher(): JSX.Element | null {
  const { account } = useAccount()
  const { organizations, currentOrganizationId, switchOrganization, loading } =
    useOrganizationSwitcherContext()

  const currentOrganization = account?.currentOrganization ?? null
  const userEmail = account?.email

  const handleSelectOrganization = (organizationId: string): void => {
    void switchOrganization(organizationId)
  }

  if (loading || !currentOrganization) {
    return (
      <div className={styles.loading}>
        <Skeleton width="100%" height="40px" />
      </div>
    )
  }

  // Single organization - show as non-interactive item
  if (organizations.length <= 1) {
    return (
      <OrganizationSwitcherWrapper email={userEmail}>
        <OrganizationItemContent
          className={styles.staticDropdownMenuItem}
          name={currentOrganization.name}
          channelCount={countEnabledChannels(currentOrganization.channels)}
          planName={
            currentOrganization.billing?.__typename === 'OBBilling'
              ? (currentOrganization.billing.subscription?.plan?.id ?? null)
              : null
          }
          isOwnedByCurrentUser={userEmail === currentOrganization.ownerEmail}
        />
      </OrganizationSwitcherWrapper>
    )
  }

  // Two organizations - show flat list with radio buttons
  if (organizations.length === 2) {
    return (
      <OrganizationSwitcherWrapper email={userEmail}>
        <DropdownMenu.RadioGroup
          aria-label="Choose organization"
          value={currentOrganizationId ?? ''}
        >
          {organizations.map((org) => (
            <DropdownMenu.RadioItem
              key={org.id}
              value={org.id}
              onSelect={(): void => handleSelectOrganization(org.id)}
            >
              <OrganizationItemContent
                name={org.name}
                channelCount={countEnabledChannels(org.channels)}
                memberCount={org.members?.totalCount ?? 0}
                planName={
                  org.billing?.__typename === 'OBBilling'
                    ? (org.billing.subscription?.plan?.name ?? null)
                    : null
                }
                isOwnedByCurrentUser={userEmail === org.ownerEmail}
              />
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </OrganizationSwitcherWrapper>
    )
  }

  // Three or more organizations - show as submenu
  const currentOrganizationChannelCount = countEnabledChannels(
    currentOrganization.channels,
  )

  return (
    <OrganizationSwitcherWrapper email={userEmail}>
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <Flex direction="column">
            <Heading size="xs" as="p" translate="no">
              {currentOrganization.name}
            </Heading>
            <Text as="small" color="subtle" size="sm">
              {currentOrganization.billing?.__typename === 'OBBilling' &&
                currentOrganization.billing.subscription?.plan?.id && (
                  <>
                    {formatPlanName(
                      currentOrganization.billing.subscription.plan.id,
                    )}{' '}
                    &middot;{' '}
                  </>
                )}
              {currentOrganizationChannelCount === 1
                ? '1 channel'
                : `${currentOrganizationChannelCount} channels`}
            </Text>
          </Flex>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubMenu className={styles.subMenu}>
          <DropdownMenu.Label>Organizations</DropdownMenu.Label>
          <DropdownMenu.Separator />

          <DropdownMenu.RadioGroup
            aria-label="Choose organization"
            value={currentOrganizationId ?? ''}
          >
            {organizations.map((org) => (
              <DropdownMenu.RadioItem
                key={org.id}
                value={org.id}
                onSelect={(): void => handleSelectOrganization(org.id)}
              >
                <OrganizationItemContent
                  name={org.name}
                  channelCount={countEnabledChannels(org.channels)}
                  memberCount={org.members?.totalCount}
                  planName={
                    org.billing?.__typename === 'OBBilling'
                      ? (org.billing.subscription?.plan?.name ?? null)
                      : null
                  }
                  isOwnedByCurrentUser={userEmail === org.ownerEmail}
                />
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.SubMenu>
      </DropdownMenu.Sub>
    </OrganizationSwitcherWrapper>
  )
}
